<template>
  <Dialog ref="dialogRef" :expanderClass="'d-inline'">
    <template #expander="{ openModal }">
      <button
        type="button"
        class="loc-trigger"
        @click="openModal"
      >
        <PhMapPin :size="16" weight="bold" />
        <span class="loc-trigger__label">{{ triggerLabel }}</span>
      </button>
    </template>

    <template #dialog.title>
      Location Information
    </template>

    <template #dialog.content="{ closeModal }">
      <div class="loc-modal">
        <div class="loc-modal__fields">
          <div class="loc-row">
            <span class="loc-row__label">Name / Store Number</span>
            <span class="loc-row__value">{{ location?.name || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Country</span>
            <span class="loc-row__value">{{ location?.country || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">City</span>
            <span class="loc-row__value">{{ location?.city || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">State</span>
            <span class="loc-row__value">{{ location?.state || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Zip</span>
            <span class="loc-row__value">{{ location?.zip || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Region</span>
            <span class="loc-row__value">{{ location?.region || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Contact Name</span>
            <span class="loc-row__value">{{ location?.contactName || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Phone Number</span>
            <span class="loc-row__value">{{ location?.phoneNumber || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Hours</span>
            <span class="loc-row__value">{{ location?.hours || '—' }}</span>
          </div>
          <div class="loc-row">
            <span class="loc-row__label">Time Zone</span>
            <span class="loc-row__value">{{ location?.timezone || '—' }}</span>
          </div>
        </div>

        <div class="loc-modal__footer">
          <ButtonCustomized label="Close" ctaSecondary @click="closeModal" />
          <SelectLocationDialog
            v-if="!readOnly"
            btnTitle="Change Location"
            cta
            @save="onLocationChange($event, closeModal)"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { PhMapPin } from '@phosphor-icons/vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import ButtonCustomized from '@/components/form-fields/ButtonCustomized.vue';
import SelectLocationDialog from '@/views/PlayersListView/CreatePlayerForm/SelectLocationDialog.vue';

const props = defineProps<{
  location?: any;
  readOnly?: boolean;
}>();

const emit = defineEmits<{ change: [location: any] }>();

const dialogRef = ref();

const triggerLabel = computed(() => {
  const name = props.location?.name;
  if (name && typeof name === 'string' && name.trim().length > 0) return name;
  return 'Location Info';
});

function onLocationChange(newLocation: any, closeOuter: () => void) {
  emit('change', newLocation);
  closeOuter();
}
</script>

<style lang="scss" scoped>
.loc-trigger {
  /* BUI tertiary 52px pill (Funnel / Columns / Refresh family). */
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: var(--bui-cta-h, 52px);
  padding: 0 var(--bui-ter-pad, 14px);
  background: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
  color: var(--bui-ter-text, #ffd9e6);
  border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
  border-radius: var(--bui-ter-radius, 8px);
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  max-width: 240px;

  :deep(svg) {
    color: var(--bui-ter-icon, #fffcfd);
  }

  &:hover {
    background: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
  }
}

.loc-trigger__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loc-modal {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 420px;
  max-width: 560px;
  color: var(--bui-tbl-row-text, #2a2a30);
}

.loc-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.loc-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--bui-tbl-line, #e8e8ec);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
}

.loc-row__label {
  font-weight: 700;
  color: var(--bui-tbl-row-text-strong, var(--bui-tbl-row-text, #2a2a30));
  flex-shrink: 0;
}

.loc-row__value {
  color: var(--bui-tbl-detail-text, #5c5c66);
  text-align: right;
  word-break: break-word;
}

.loc-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
</style>
